/**
 * NotesAndTagsTab — Tab 2 des Card-Detail-Modal (CARD-03, CARD-04).
 *
 * Section A: Notes-Textarea + Save-Button → PATCH /leads/:id { notes }
 * Section B: Tag-Editor
 *   - Hotlead-Toggle (Switch) → addTag/removeTag('hotlead')
 *   - Custom-Tag-Chips mit X-Remove-Icon
 *   - Tag-Input mit Enter → addTag(value)
 *
 * onDirty(true/false) wird gemeldet wenn Notes-Textarea ungespeicherte
 * Aenderungen hat.
 *
 * CI-Farben: #E9FF57 Background + #1A1A1A Text auf Save / Hotlead-Toggle.
 */

import { useState, useEffect, type KeyboardEvent } from 'react';
import { X, Star } from 'lucide-react';
import { useLeadBoard } from '../../store/useLeadBoard';
import type { Lead } from '../../types';

interface Props {
  lead: Lead;
  onSaved: (l: Lead) => void;
  onDirty?: (dirty: boolean) => void;
}

const HOTLEAD_TAG = 'hotlead';

export default function NotesAndTagsTab({ lead, onSaved, onDirty }: Props) {
  const updateLead = useLeadBoard((s) => s.updateLead);
  const addTag = useLeadBoard((s) => s.addTag);
  const removeTag = useLeadBoard((s) => s.removeTag);

  const [notes, setNotes] = useState<string>(lead.notes ?? '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  const [newTag, setNewTag] = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);

  // Reset notes wenn ein neuer Lead reinkommt
  useEffect(() => {
    setNotes(lead.notes ?? '');
    setNotesError(null);
    setTagError(null);
  }, [lead.id]);

  const isDirty = notes !== (lead.notes ?? '');

  useEffect(() => {
    onDirty?.(isDirty);
  }, [isDirty, onDirty]);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    setNotesError(null);
    try {
      const updated = await updateLead(lead.id, { notes: notes });
      onSaved(updated);
      onDirty?.(false);
    } catch (e: any) {
      setNotesError(e?.message ?? 'Notiz konnte nicht gespeichert werden');
    } finally {
      setSavingNotes(false);
    }
  };

  const hasHotlead = lead.tags.includes(HOTLEAD_TAG);

  const toggleHotlead = async () => {
    setTagBusy(true);
    setTagError(null);
    try {
      const updated = hasHotlead
        ? await removeTag(lead.id, HOTLEAD_TAG)
        : await addTag(lead.id, HOTLEAD_TAG);
      onSaved(updated);
    } catch (e: any) {
      setTagError(e?.message ?? 'Hotlead-Toggle fehlgeschlagen');
    } finally {
      setTagBusy(false);
    }
  };

  const handleAddTag = async () => {
    const trimmed = newTag.trim();
    if (trimmed.length === 0) return;
    if (trimmed.toLowerCase() === HOTLEAD_TAG) {
      setTagError(
        'Bitte den Hotlead-Toggle oben nutzen — er setzt den System-Tag korrekt.',
      );
      return;
    }
    if (lead.tags.includes(trimmed)) {
      setTagError(`Tag "${trimmed}" existiert bereits.`);
      return;
    }
    setTagBusy(true);
    setTagError(null);
    try {
      const updated = await addTag(lead.id, trimmed);
      onSaved(updated);
      setNewTag('');
    } catch (e: any) {
      setTagError(e?.message ?? 'Tag konnte nicht hinzugefuegt werden');
    } finally {
      setTagBusy(false);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    setTagBusy(true);
    setTagError(null);
    try {
      const updated = await removeTag(lead.id, tag);
      onSaved(updated);
    } catch (e: any) {
      setTagError(e?.message ?? 'Tag konnte nicht entfernt werden');
    } finally {
      setTagBusy(false);
    }
  };

  const onTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const customTags = lead.tags.filter((t) => t !== HOTLEAD_TAG);

  return (
    <div className="space-y-6">
      {/* Section A: Notes */}
      <section>
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">Notiz</h3>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={6}
          placeholder="Interne Notizen zum Lead..."
          className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-sm text-[#1A1A1A] resize-y"
        />
        {notesError && (
          <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            {notesError}
          </div>
        )}
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={handleSaveNotes}
            disabled={savingNotes || !isDirty}
            className="bg-[#E9FF57] text-[#1A1A1A] px-4 py-2 rounded font-medium hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingNotes ? 'Speichert...' : 'Notiz speichern'}
          </button>
        </div>
      </section>

      {/* Section B: Tags */}
      <section className="border-t border-[#DAD9D9] pt-4">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">Tags</h3>

        {/* Hotlead-Toggle */}
        <div className="flex items-center justify-between mb-3 p-2 bg-gray-50 rounded">
          <div className="flex items-center gap-2">
            <Star
              className={`w-4 h-4 ${hasHotlead ? 'text-[#E9FF57] fill-[#E9FF57]' : 'text-gray-400'}`}
            />
            <span className="text-sm text-[#1A1A1A] font-medium">Hotlead</span>
          </div>
          <button
            type="button"
            onClick={toggleHotlead}
            disabled={tagBusy}
            className={
              hasHotlead
                ? 'bg-[#E9FF57] text-[#1A1A1A] px-3 py-1 rounded text-xs font-medium disabled:opacity-50'
                : 'bg-gray-200 text-[#1A1A1A] px-3 py-1 rounded text-xs font-medium hover:bg-gray-300 disabled:opacity-50'
            }
            aria-pressed={hasHotlead}
          >
            {hasHotlead ? 'Aktiv' : 'Aktivieren'}
          </button>
        </div>

        {/* Custom-Tag-Chips */}
        <div className="flex flex-wrap gap-2 mb-2">
          {customTags.length === 0 && (
            <span className="text-xs text-gray-500 italic">Keine weiteren Tags</span>
          )}
          {customTags.map((tag) => (
            <span
              key={tag}
              className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs flex items-center gap-1"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                disabled={tagBusy}
                className="hover:bg-gray-200 rounded p-0.5"
                aria-label={`Tag ${tag} entfernen`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>

        {/* Tag-Input */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={onTagKeyDown}
            placeholder="Neuen Tag (Enter zum Hinzufuegen)"
            className="flex-1 border border-[#DAD9D9] rounded px-3 py-1.5 text-sm text-[#1A1A1A]"
          />
          <button
            type="button"
            onClick={handleAddTag}
            disabled={tagBusy || newTag.trim().length === 0}
            className="bg-[#E9FF57] text-[#1A1A1A] px-3 py-1.5 rounded text-sm font-medium hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Hinzufuegen
          </button>
        </div>

        {tagError && (
          <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            {tagError}
          </div>
        )}
      </section>
    </div>
  );
}
